import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { format, parseISO, endOfWeek, formatISO } from 'date-fns';
import { css, styled } from 'styled-components';
import { Help, Play } from '@hn-ui/icons';
import { Spinner } from '@hn-ui/loaders';
import { Tooltip } from '@hn-ui/tooltips';
import { CheckBox } from '../GetStarted/CheckBoxes/index';
import DeletedCards from './DeletedCards';
import { getActivityStream } from './StudentActivityStreamApi';
import { createTimeText } from './utils';
import {
  TasksTableHeader,
  AssignmentList,
  useAssignmentsStreamData,
} from '../ActivityStreamDashboard/shared';
import { usePostSeenCardsSessions } from '../Common/hooks/usePostSeenCardsSessions';
import { StruggleIcon } from '../Common/components/StruggleIcon';
import {
  useWeeklyDashboardAnalytics,
  WeeklyDashboardEvents,
} from './hooks/useWeeklyDashboardAnalytics';

const FILTER_ROW_HEIGHT = 57;
const BASE_OFFSET = 67;
const PAST_WEEK_OFFSET = 40;
const CONTAINER_PADDING = {
  top: 16,
  right: 36,
  bottom: 0,
  left: 40,
};

const stickyCss = (topOffset, isWeekInThePast, withFilter) => css`
  position: sticky;
  top: ${topOffset +
  (isWeekInThePast ? PAST_WEEK_OFFSET : 0) +
  (withFilter ? FILTER_ROW_HEIGHT : 0)}px;
`;

const getContainerPaddingCss = (reset) => css`
  padding: ${CONTAINER_PADDING.top}px ${CONTAINER_PADDING.right}px
    ${CONTAINER_PADDING.bottom}px ${CONTAINER_PADDING.left}px;

  ${reset &&
  css`
    margin: -${CONTAINER_PADDING.top}px -${CONTAINER_PADDING.right}px -${CONTAINER_PADDING.bottom}px -${CONTAINER_PADDING.left}px;
  `}
`;

const ScCheckBox = styled(CheckBox)`
  & > div {
    width: 20px;
    height: 20px;

    margin-right: 8px;
  }
  && > label {
    color: ${({ disabled, theme }) =>
      disabled ? theme.colors.disabled : theme.colors.blackText};
  }
`;

const Container = styled.section`
  width: 100%;

  box-sizing: border-box;
  ${getContainerPaddingCss()};

  background: #f3faff;
  margin-bottom: 32px;
`;

const CheckBoxRow = styled.div`
  width: 100%;

  ${({ $isWeekInThePast }) => stickyCss(BASE_OFFSET, $isWeekInThePast)};
  z-index: 10;

  display: flex;

  ${getContainerPaddingCss(true)};
  padding-bottom: 16px;

  background: #f3faff;
  border-bottom: 1px #cce8f9 solid;
  box-shadow: inset rgb(0 0 0 / 7%) 0px 26px 10px -26px;
`;

const ActivitiesRow = styled.div`
  display: grid;
  grid-template-columns: 420px 80px 100px 90px 115px;
  column-gap: 3%;

  &:last-child {
    padding-bottom: 20px;
  }
`;

const ActivitiesHeadingRow = styled(TasksTableHeader)`
  width: 100%;

  ${({ $isWeekInThePast, $withFilter }) =>
    stickyCss(BASE_OFFSET, $isWeekInThePast, $withFilter)};
  z-index: 8;
  padding-top: 16px;
  background: #f3faff;
`;

const ScAssignmentList = styled(AssignmentList)`
  [data-name='tasks-header'] {
    ${({ $isWeekInThePast, $withFilter }) =>
      stickyCss(BASE_OFFSET, $isWeekInThePast, $withFilter)};
    z-index: 9;
    padding-top: 16px;

    ${({ hideColumnsHeading }) =>
      hideColumnsHeading
        ? css`
            > *:first-child {
              background: #f3faff;
            }
          `
        : css`
            background: #f3faff;
          `}
  }
`;

const ActivitiesTxt = styled.p`
  position: relative;

  margin-bottom: 16px;

  color: ${({ $struggle }) => ($struggle ? '#DE2D68' : '#242527')};
  font-family: var(--main-font);
  font-size: 16px;
  line-height: 18px;
  text-decoration: none;

  transition: 0.2s color ease-in-out;
`;

const ActivitiesTxtAlignRight = styled(ActivitiesTxt)`
  text-align: right;
`;

const LoadingText = styled(ActivitiesTxt)`
  margin: 18px 0 16px;
`;

const CardNamePrefix = styled(ActivitiesTxt)``;

const HelpIcon = styled(Help)`
  margin-left: 4px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PlayIcon = styled(Play)`
  margin-top: -3px;
`;

const ActivitiesTask = styled.a`
  display: grid;
  grid-template-columns: 28px 35px auto;
  justify-content: start;
  justify-items: start;

  cursor: pointer;
  text-decoration: none;

  &:hover ${ActivitiesTxt} {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
  &:hover path {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const ModuleHeading = styled.h3`
  color: #777b8b;
  font-family: var(--main-font);
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
`;

const ModuleHeadingPos = styled(ModuleHeading)`
  width: fit-content;

  margin-top: 24px;
  margin-bottom: 16px;
`;

const TopicHeading = styled.h3`
  color: ${({ theme }) => theme.colors.black40};
  font-family: var(--main-font);
  font-size: 16px;
  line-height: 24px;
`;

const TopicHeadingPos = styled(TopicHeading)`
  width: fit-content;
  display: flex;

  margin-top: 16px;
  margin-bottom: 24px;
`;

const ScStruggleIcon = styled(StruggleIcon)`
  position: absolute;
  top: 9px;
  right: -4px;
  transform: translate(100%, -50%);
`;

const AssignmentsOutline = styled.div`
  padding-top: 7px;

  border-top: ${({ theme }) => `1px solid ${theme.colors.black90}`};
`;

const ActivityTextWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  padding-bottom: 12px;
`;

const ScSpinner = styled(Spinner)`
  padding-bottom: 24px;
`;

const TooltipDescription = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: 0.1px;
`;

export const StudentActivityStream = ({
  role,
  schoolId,
  studentId,
  chosenDate,
  ISODate,
  isWeekInThePast,
  ...rest
}) => {
  const [streams, setStreams] = useState([]);
  const [isCheck, setIsCheck] = useState(false);
  const [isActivityStreamLoading, setIsActivityStreamLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const myRef = useRef(null);
  const offsetTop = isWeekInThePast ? 107 : 67;
  const scrollToRef = useCallback(
    (ref) => {
      if (ref.current.getBoundingClientRect().top < offsetTop) {
        ref.current.scrollIntoView();
        window.scrollBy(0, -offsetTop);
      }
    },
    [myRef]
  );

  const requestParams = useMemo(
    () => ({
      studentId,
      startDate: formatISO(chosenDate, { representation: 'date' }),
      endDate: formatISO(endOfWeek(chosenDate, { weekStartsOn: 1 }), {
        representation: 'date',
      }),
      role,
      schoolId,
    }),
    [chosenDate, studentId, role, schoolId]
  );

  const [assignmentsWithSummary, { state: assignmentsState }] =
    useAssignmentsStreamData({ requestParams });
  const assignments = assignmentsWithSummary?.assignments ?? [];
  const [markCardsAsSeen] = usePostSeenCardsSessions();

  useEffect(() => {
    const loadData = async (id, date) => {
      setIsActivityStreamLoading(true);
      try {
        const startDate = formatISO(date, { representation: 'date' });
        const endDate = formatISO(endOfWeek(date, { weekStartsOn: 1 }), {
          representation: 'date',
        });
        const data = await getActivityStream(id, startDate, endDate);
        setIsActivityStreamLoading(false);
        setStreams(data);
      } catch (error) {
        setIsError(true);
        setIsActivityStreamLoading(false);
        if (window.Sentry) window.Sentry.captureException(error);
      }
    };
    if (!isActivityStreamLoading) loadData(studentId, chosenDate);
  }, [studentId, chosenDate]);

  useEffect(() => {
    const activeStruggleCards = streams
      .map((stream) => {
        const struggleArr = [];
        stream.cards.forEach((card) => {
          if (
            card.struggle &&
            !stream.seenNotifications.includes(card.sessionId)
          )
            struggleArr.push(card.sessionId);
        });
        return struggleArr;
      })
      .flat();

    if (activeStruggleCards.length !== 0) {
      markCardsAsSeen(studentId, activeStruggleCards);
    }
  }, [streams]);

  const struggledStreams = useMemo(
    () =>
      streams
        .filter((el) => el.struggleTasks)
        .map((el) => ({
          ...el,
          cards: el.cards.filter((card) => card.struggle),
        })),
    [streams]
  );

  const struggledAssignments = useMemo(
    () =>
      assignments
        .filter((el) => el.cards.some((card) => card.struggled))
        .map((el) => ({
          ...el,
          cards: el.cards.filter((card) => card.struggled),
        })),
    [assignments]
  );

  let prevGradeAndModule = '';
  const struggleTasksCount =
    streams.reduce((sum, el) => sum + el.struggleTasks, 0) +
    (assignmentsWithSummary?.struggleTasksCount ?? 0);

  const completedTasksCount =
    streams.reduce((sum, el) => sum + el.tasksCompleted, 0) +
    (assignments?.reduce((sum, el) => sum + el.cards.length, 0) ?? 0);

  const chosenStreams = isCheck ? struggledStreams : streams;
  const chosenAssignments = isCheck ? struggledAssignments : assignments;

  const hasStreams = chosenStreams.length > 0;
  const hasAssignments = chosenAssignments.length > 0;
  const noTasks = !hasStreams && !hasAssignments;
  const showFilter =
    struggleTasksCount > 0 && struggleTasksCount < completedTasksCount;
  const sendGaEvent = useWeeklyDashboardAnalytics();
  const isLoading = isActivityStreamLoading || assignmentsState === 'loading';
  return (
    <Container ref={myRef} {...rest}>
      {showFilter && (
        <CheckBoxRow $isWeekInThePast={isWeekInThePast}>
          <ScCheckBox
            checked={isCheck}
            disabled={noTasks || !struggleTasksCount}
            label={`Show only struggle tasks (${struggleTasksCount})`}
            onChecked={() => {
              if (!isCheck) {
                sendGaEvent(WeeklyDashboardEvents.studentStruggleFilterEnable);
              }
              setIsCheck(!isCheck);
              scrollToRef(myRef);
            }}
          />
        </CheckBoxRow>
      )}
      {isLoading && (
        <ScSpinner icon={{ size: 30 }} text={{ label: 'Loading...' }} />
      )}
      {isError && <LoadingText>Server Error!</LoadingText>}
      {hasStreams && (
        <>
          <ActivitiesHeadingRow
            withoutLeftPadding
            $isWeekInThePast={isWeekInThePast}
            $withFilter={showFilter}
            isWeekly
          />
          {chosenStreams.map(
            ({
              title,
              description,
              grade,
              module,
              moduleDescription,
              cards,
              deletedCards,
              seenNotifications,
            }) => {
              const visibleGradeAndModule =
                `${grade}. ${module}.` === prevGradeAndModule
                  ? ''
                  : `${grade}. ${module}.`;
              prevGradeAndModule = `${grade}. ${module}.`;
              let isBeginNewModule = false;
              try {
                const firstCardPrefix = cards[0].cardNamePrefix;
                isBeginNewModule =
                  firstCardPrefix.slice(firstCardPrefix.length - 3) === 'A.A';
              } catch (error) {
                if (window.Sentry)
                  window.Sentry.captureEvent({
                    message:
                      'Weekly Dashboard: Cannot read properties of undefined (reading "slice")',
                    level: 'info',
                    extra: {
                      title,
                      description,
                      grade,
                      module,
                      moduleDescription,
                      cards,
                      deletedCards,
                      seenNotifications,
                    },
                  });
              }

              return (
                <React.Fragment key={title}>
                  {isBeginNewModule && (
                    <Tooltip
                      maxWidth={450}
                      content={
                        <TooltipDescription>
                          {moduleDescription}
                        </TooltipDescription>
                      }
                    >
                      <ModuleHeadingPos>
                        {visibleGradeAndModule}
                      </ModuleHeadingPos>
                    </Tooltip>
                  )}

                  <TopicHeadingPos>
                    {title}
                    <Tooltip
                      maxWidth={302}
                      content={
                        <TooltipDescription>{description}</TooltipDescription>
                      }
                    >
                      <HelpIcon color='black70' />
                    </Tooltip>
                  </TopicHeadingPos>

                  {cards.map(
                    ({
                      cardLetter,
                      cardName,
                      cardId,
                      cardLink,
                      sessionId,
                      date,
                      completed: isCompleted,
                      timeSpent,
                      accuracy,
                      struggle,
                      exerciseFinished,
                      exerciseFinishedSucc,
                    }) => {
                      const seen = seenNotifications.includes(sessionId);
                      return (
                        <ActivitiesRow key={cardId}>
                          <ActivitiesTask
                            href={cardLink}
                            target='_blank'
                            rel='noreferrer'
                            onClick={() => {
                              sendGaEvent(
                                struggle
                                  ? WeeklyDashboardEvents.struggleCardClick
                                  : WeeklyDashboardEvents.cardClick
                              );
                            }}
                          >
                            <PlayIcon />
                            <CardNamePrefix>{`${cardLetter}.`}</CardNamePrefix>
                            <ActivitiesTxt>{cardName}</ActivitiesTxt>
                          </ActivitiesTask>

                          <ActivitiesTxtAlignRight>
                            {format(parseISO(date), 'MMM d')}
                          </ActivitiesTxtAlignRight>

                          <ActivitiesTxtAlignRight>
                            {isCompleted ? 'completed' : 'in progress'}
                          </ActivitiesTxtAlignRight>

                          <ActivitiesTxtAlignRight>
                            {createTimeText(timeSpent)}
                          </ActivitiesTxtAlignRight>

                          <ActivitiesTxtAlignRight $struggle={struggle}>
                            {`${exerciseFinishedSucc}/${exerciseFinished} (${accuracy}%)`}
                            {struggle && (
                              <ScStruggleIcon
                                seen={seen}
                                struggledAccuracy
                                onMouseEnter={() => {
                                  sendGaEvent(
                                    WeeklyDashboardEvents.studentCardIconHover
                                  );
                                }}
                              />
                            )}
                          </ActivitiesTxtAlignRight>
                        </ActivitiesRow>
                      );
                    }
                  )}
                  {!isCheck && deletedCards && (
                    <DeletedCards
                      data={deletedCards}
                      hasActiveCards={cards.length > 0}
                    />
                  )}
                </React.Fragment>
              );
            }
          )}
        </>
      )}
      {!isLoading && !isError && noTasks && (
        <ActivityTextWrapper>
          <ActivitiesTxt>No tasks completed during the week.</ActivitiesTxt>
        </ActivityTextWrapper>
      )}
      {hasAssignments && (
        <>
          {(hasStreams || isLoading || isError) && <AssignmentsOutline />}
          <ScAssignmentList
            studentId={studentId}
            assignments={chosenAssignments}
            withoutLeftPadding
            hideColumnsHeading={hasStreams}
            isWeekly
            $isWeekInThePast={isWeekInThePast}
            $withFilter={showFilter}
          />
        </>
      )}
    </Container>
  );
};

export default StudentActivityStream;
